import type {
    BaseQueryApi,
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
    AddMessageRequest,
    AddMessageResponse,
    GetProfileResponse,
    InitiateThreadRequest,
    InitiateThreadResponse,
    FirebaseIdToken,
    FirebaseRefreshedIdToken
} from 'tradera/components/messaging/firebase.types';
import { isomorphicWebApiBaseQuery } from 'tradera/state/services/utils/webapi-base-query';
import type { RootState } from '../../state/configure-store';
import { receivedFirebaseIdToken } from 'tradera/state/auth/reducer';
import { memberApi } from 'tradera/state/services/member-api';
import type { FirebaseConfiguration } from 'tradera/state/services/types/webapi-member-generated';

const identitytoolkitBaseQuery = fetchBaseQuery({
    prepareHeaders: (headers) => {
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        return headers;
    },
    baseUrl: 'https://identitytoolkit.googleapis.com/v1/'
});

export const firebaseBaseQueryFn: BaseQueryFn<
    FetchArgs & { firebaseConfiguration: FirebaseConfiguration },
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    const firebaseConfiguration = args?.firebaseConfiguration;
    if (!firebaseConfiguration) {
        return {
            error: {
                status: 400,
                statusText: 'Bad Request',
                data: 'No Messaging configuration found'
            }
        };
    }

    const baseUrl = `https://${firebaseConfiguration.region}-${firebaseConfiguration.projectId}.cloudfunctions.net`;
    return firebaseBaseQuery(baseUrl)(args, api, extraOptions);
};

const firebaseBaseQuery = (baseUrl: string) =>
    fetchBaseQuery({
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as RootState).auth.firebaseIdToken;
            if (token) {
                headers.set('authorization', `Bearer ${token.idToken}`);
            }
            headers.set('Accept', 'application/json');
            headers.set('Content-Type', 'application/json');
            return headers;
        },
        baseUrl
    });

const getToken = async (
    api: BaseQueryApi,
    extraOptions: object,
    firebaseConfiguration: FirebaseConfiguration
): Promise<FirebaseIdToken> => {
    const customTokenResult = (await isomorphicWebApiBaseQuery(
        {
            url: '/api/webapi/auth/firebase-token',
            method: 'POST',
            body: { projectId: firebaseConfiguration.projectId }
        },
        api,
        extraOptions
    )) as { data?: { token: string } };

    const result = await identitytoolkitBaseQuery(
        {
            url: `accounts:signInWithCustomToken?key=${firebaseConfiguration.apiKey}`,
            method: 'POST',
            body: {
                token: customTokenResult.data?.token ?? '',
                returnSecureToken: true
            }
        },
        api,
        extraOptions
    );
    return result.data as FirebaseIdToken;
};

export const firebaseApi = createApi({
    reducerPath: 'firebaseApi',
    baseQuery: async (args, api, extraOptions) => {
        const currentIdToken = (api.getState() as RootState).auth
            .firebaseIdToken;
        const { data: messagingConfiguration = {} } = await api.dispatch(
            memberApi.endpoints.getMessagingConfiguration.initiate(undefined)
        );
        const firebaseConfiguration =
            messagingConfiguration?.firebaseConfiguration;
        if (!firebaseConfiguration) {
            return {
                error: {
                    status: 400,
                    statusText: 'Bad Request',
                    data: 'No Messaging configuration found'
                }
            };
        }
        const argsWithFirebaseConfiguration = {
            ...args,
            firebaseConfiguration
        };

        let result;
        if (currentIdToken?.idToken) {
            result = await firebaseBaseQueryFn(
                argsWithFirebaseConfiguration,
                api,
                extraOptions
            );
            if (result.error && result.error.status === 401) {
                const refreshedIdTokenQuery = await identitytoolkitBaseQuery(
                    {
                        url: `token?key=${firebaseConfiguration.apiKey}`,
                        method: 'POST',
                        body: {
                            grant_type: 'refresh_token',
                            refresh_token: currentIdToken.refreshToken
                        }
                    },
                    api,
                    extraOptions
                );

                //Not sure why but when refreshing the token the object names have underscores instead of camelCase
                const refreshedTokenData =
                    refreshedIdTokenQuery?.data as FirebaseRefreshedIdToken;
                api.dispatch(
                    receivedFirebaseIdToken({
                        idToken: refreshedTokenData.id_token,
                        refreshToken: refreshedTokenData.refresh_token,
                        expiresIn: refreshedTokenData.expires_in
                    })
                );
                result = await firebaseBaseQueryFn(
                    argsWithFirebaseConfiguration,
                    api,
                    extraOptions
                );
            }
        } else {
            const token = await getToken(
                api,
                extraOptions,
                firebaseConfiguration
            );
            api.dispatch(receivedFirebaseIdToken(token));
            result = await firebaseBaseQueryFn(
                argsWithFirebaseConfiguration,
                api,
                extraOptions
            );
        }
        return result;
    },
    endpoints: (builder) => ({
        addMessage: builder.mutation<AddMessageResponse, AddMessageRequest>({
            query: (parameters) => {
                return {
                    url: '/addMessage',
                    method: 'POST',
                    body: { data: parameters.data }
                };
            }
        }),
        initiateThread: builder.mutation<
            InitiateThreadResponse,
            InitiateThreadRequest
        >({
            query: (parameters) => {
                return {
                    url: '/initiateThread',
                    method: 'POST',
                    body: { data: parameters.data }
                };
            }
        }),
        getProfile: builder.query<GetProfileResponse, undefined>({
            query: () => {
                return {
                    url: '/getProfile',
                    method: 'POST',
                    //Firebase http function requires a body
                    body: { data: {} }
                };
            }
        })
    })
});

export const {
    useAddMessageMutation,
    useInitiateThreadMutation,
    useGetProfileQuery
} = firebaseApi;
